import React, { useCallback, useMemo } from 'react';
import { type Href, Link, usePathname, useSegments } from 'expo-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { Portal } from '@gorhom/portal';

import {
  BodyText,
  Button,
  Navbar,
  theme,
  useNavbarContainerHeight,
  useResponsive,
  useToggleState,
  VStack,
} from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';
import { useUsableNetBannerOffset } from '@order/usable-net';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useLastInteractedStore } from '../../../../context/LastInteractedStore';
import { useBagCount, useGroupOrderDetails, usePendingOrder } from '../../../../hooks';
import { useActiveScreenOptions } from '../../../../routing/hooks/useActiveScreenOptions';
import { LocationTooltip, useLocationTooltip } from '../../../LocationTooltip';
import { LINKS, ROUTE_GROUPS, TABS } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';
import { NavbarMoreContentLink } from './components/NavbarMoreContentLink';
import { useNavbarContent, useNavbarLocationData, useNavbarNewContentIndicator } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarSM = () => {
  const pathname = usePathname();
  const segments = useSegments();

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { minWidth } = useResponsive();
  const { heightStyle: navbarHeightStyle } = useNavbarContainerHeight();
  const usableNetBannerOffset = useUsableNetBannerOffset();

  const { bagCount } = useBagCount();
  const pendingOrder = usePendingOrder({ requestPolicy: 'cache-only' });
  const { hostGroupOrderDetails, inviteeGroupOrderDetails } = useGroupOrderDetails();
  const isLoggedIn = useIsLoggedIn();
  const lastInteractedStore = useLastInteractedStore();

  const {
    locationEyebrow,
    locationTitle,
    locationTooltipChannel,
    locationTooltipId,
    locationTooltipTarget,
    menuHref,
    orderChannel,
  } = useNavbarLocationData({ lastInteractedStore });

  // ─── State ───────────────────────────────────────────────────────────

  const { value: isMoreOpen, toggle: toggleMore, toggleOff: closeMore } = useToggleState();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { moreMenuNavLinks } = useNavbarContent();
  const moreMenuNavLinkTitles = useMemo(
    () => moreMenuNavLinks.map((link) => link.title),
    [moreMenuNavLinks],
  );
  const { unseenLinks, hasNewContent, storeLastSeenLinks } = useNavbarNewContentIndicator(moreMenuNavLinkTitles); // prettier-ignore

  // ─── Navigation ──────────────────────────────────────────────────────

  const screenOptions = useActiveScreenOptions();
  const navbarLayout = screenOptions?.navbarLayout ?? 'floating';

  const shouldRenderStaticContainer = navbarLayout !== 'floating';
  const shouldRenderStaticContainerBorder = navbarLayout === 'static-with-border';
  const shouldShowGlassShadow = navbarLayout === 'floating';
  const shouldRenderCompactNav = !minWidth.isMD;

  const locationSelectionHref = useMemo<Href>(
    () => ({ pathname: LINKS.LOCATION_SELECTION, params: { backToPath: pathname } }),
    [pathname],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isMenuActive = segments.some((segment) => segment === TABS.MENU);
  const isRewardsActive = segments.some((segment) => segment === TABS.REWARDS);
  const isHomeActive = segments.some((segment) => segment === TABS.HOME || segment === TABS.HOME_SINGLE); // prettier-ignore
  const isLocationSelectionActive =
    pathname === LINKS.LOCATION_SELECTION || pathname === LINKS.LOCATIONS;
  const isCustomizationActive = segments.some((segment) => segment === ROUTE_GROUPS.CUSTOMIZATION); // prettier-ignore
  const shouldDisableLocationChange = isLocationSelectionActive || isCustomizationActive;

  const shouldShowCurrentOrder = isHomeActive && Boolean(pendingOrder);

  const shouldShowGroupOrderHost = !shouldShowCurrentOrder && Boolean(hostGroupOrderDetails);
  const shouldShowGroupOrderInvitee = !shouldShowCurrentOrder && Boolean(inviteeGroupOrderDetails);
  const shouldShowLocation =
    !shouldShowCurrentOrder && !shouldShowGroupOrderHost && !shouldShowGroupOrderInvitee;

  const locationTooltip = useLocationTooltip({
    canShow: isHomeActive && shouldShowLocation,
    channel: locationTooltipChannel,
    locationId: locationTooltipId,
    targetPosition: locationTooltipTarget,
  });

  // ─── Styles ─────────────────────────────────────────────────────────

  const staticNavbarContainerStyles = [
    styles.staticNavbarContainer,
    shouldRenderStaticContainerBorder ? styles.staticNavbarContainerBorder : undefined,
    navbarHeightStyle,
  ];

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    closeMore();
    storeLastSeenLinks();
  }, [closeMore, storeLastSeenLinks]);

  const handleMorePress = useCallback(() => {
    track('nav_bar_more_selected');

    if (isMoreOpen) {
      handleClose();

      return;
    }

    toggleMore();
  }, [handleClose, isMoreOpen, toggleMore, track]);

  const handleHomePress = useCallback(() => {
    handleClose();
    track('nav_bar_home_selected');
  }, [handleClose, track]);

  const handleMenuPress = useCallback(() => {
    handleClose();
    track('nav_bar_menu_selected');
  }, [handleClose, track]);

  const handleRewardsPress = useCallback(() => {
    handleClose();
    track('nav_bar_rewards_selected');
  }, [handleClose, track]);

  const handleBagPress = useCallback(() => {
    handleClose();
    track('nav_bar_bag_selected');
  }, [handleClose, track]);

  const handleCurrentOrderPress = useCallback(() => {
    handleClose();
    track('nav_bar_order_status_selected');
    pendingOrder?.onPress();
  }, [handleClose, pendingOrder, track]);

  const handleLocationPress = useCallback(() => {
    handleClose();
    track('header_location_selected');
  }, [handleClose, track]);

  const handleLoginSignupPress = useCallback(() => {
    handleClose();
    track('nav_bar_login_signup_selected');
  }, [handleClose, track]);

  const handleMoreMenuPress = useCallback(() => {
    handleClose();
    track('nav_more_menu_selected');
  }, [handleClose, track]);

  const handleMoreRewardsPress = useCallback(() => {
    handleClose();
    track('nav_more_rewards_offers_selected');
  }, [handleClose, track]);

  const handleMoreOrderHistoryPress = useCallback(() => {
    handleClose();
    track('nav_more_order_history_selected');
  }, [handleClose, track]);

  const handleMoreAccountPress = useCallback(() => {
    handleClose();
    track('nav_more_account_selected');
  }, [handleClose, track]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Navbar.Floating.Container>
        {shouldRenderCompactNav ? (
          <Navbar.Floating.PrimaryNav shadow={shouldShowGlassShadow}>
            <Navbar.Floating.MenuButton
              accessibilityLabel={formatMessage(messages.openNavigation)}
              onPress={handleMorePress}
            />

            <Link
              href="/"
              asChild
              disabled={isHomeActive}
              aria-label={formatMessage(messages.homeAccessibilityLabel)}
            >
              <Navbar.Floating.LogoPressable onPress={handleHomePress}>
                <Navbar.Floating.Logo />
              </Navbar.Floating.LogoPressable>
            </Link>
          </Navbar.Floating.PrimaryNav>
        ) : (
          <Navbar.Floating.PrimaryNav shadow={shouldShowGlassShadow}>
            <Link
              href="/"
              asChild
              disabled={isHomeActive}
              aria-label={formatMessage(messages.homeAccessibilityLabel)}
            >
              <Navbar.Floating.LogoPressable onPress={handleHomePress}>
                <Navbar.Floating.Logo />
              </Navbar.Floating.LogoPressable>
            </Link>

            <Navbar.Floating.NavLinksList>
              <Link asChild href={menuHref}>
                <Navbar.Floating.Link
                  isActive={isMenuActive && !isMoreOpen}
                  onPress={handleMenuPress}
                >
                  <FormattedMessage {...messages.menu} />
                </Navbar.Floating.Link>
              </Link>

              <Link asChild href={LINKS.REWARDS}>
                <Navbar.Floating.Link
                  isActive={isRewardsActive && !isMoreOpen}
                  onPress={handleRewardsPress}
                >
                  <FormattedMessage {...messages.rewards} />
                </Navbar.Floating.Link>
              </Link>

              <Navbar.Floating.Link
                accessibilityRole="button"
                accessibilityState={{ expanded: isMoreOpen }}
                isActive={isMoreOpen}
                hasNewContent={hasNewContent}
                onPress={handleMorePress}
              >
                <FormattedMessage {...messages.more} />
              </Navbar.Floating.Link>
            </Navbar.Floating.NavLinksList>
          </Navbar.Floating.PrimaryNav>
        )}

        {isMoreOpen && shouldRenderCompactNav ? (
          <Portal>
            <Navbar.Floating.Drawer.Container
              accessibilityLabel={formatMessage(messages.navigationMenu)}
              backdropAccessibilityLabel={formatMessage(messages.closeNavigationBackdrop)}
              closeAccessibilityLabel={formatMessage(messages.closeNavigation)}
              onClose={handleClose}
            >
              <Navbar.Floating.Drawer.VStack>
                <Navbar.Floating.Drawer.NavLinksList>
                  <Link asChild href={menuHref}>
                    <Navbar.Floating.Drawer.NavLink onPress={handleMoreMenuPress}>
                      <FormattedMessage {...messages.menu} />
                    </Navbar.Floating.Drawer.NavLink>
                  </Link>

                  <Link asChild href={LINKS.REWARDS}>
                    <Navbar.Floating.Drawer.NavLink onPress={handleMoreRewardsPress}>
                      <FormattedMessage {...messages.rewards} />
                    </Navbar.Floating.Drawer.NavLink>
                  </Link>
                </Navbar.Floating.Drawer.NavLinksList>

                <Navbar.Floating.Drawer.NavLinksList>
                  {moreMenuNavLinks.map((link) => (
                    <NavbarMoreContentLink
                      key={link.url}
                      link={link}
                      variant="drawer"
                      onPress={handleClose}
                    />
                  ))}
                </Navbar.Floating.Drawer.NavLinksList>
              </Navbar.Floating.Drawer.VStack>

              <Navbar.Floating.Drawer.Footer>
                {isLoggedIn ? (
                  <Navbar.Floating.Drawer.NavLinksList hasDivider>
                    <Link asChild withAnchor href={LINKS.ORDER_HISTORY}>
                      <Navbar.Floating.Drawer.NavLink onPress={handleMoreOrderHistoryPress}>
                        <FormattedMessage {...messages.viewOrderHistory} />
                      </Navbar.Floating.Drawer.NavLink>
                    </Link>

                    <Link asChild href={LINKS.ACCOUNT}>
                      <Navbar.Floating.Drawer.NavLink onPress={handleMoreAccountPress}>
                        <FormattedMessage {...messages.myAccount} />
                      </Navbar.Floating.Drawer.NavLink>
                    </Link>
                  </Navbar.Floating.Drawer.NavLinksList>
                ) : (
                  <Link asChild href={LINKS.AUTH} onPress={handleLoginSignupPress}>
                    <Button accessibilityRole="link" size="large">
                      <BodyText sizeMatch={['16']} bold style={styles.drawerAuthLabel}>
                        <FormattedMessage {...messages.signInCreateAccount} />
                      </BodyText>
                    </Button>
                  </Link>
                )}
              </Navbar.Floating.Drawer.Footer>
            </Navbar.Floating.Drawer.Container>
          </Portal>
        ) : null}

        {isMoreOpen && !shouldRenderCompactNav ? (
          <Portal>
            <Navbar.Floating.Container>
              <Navbar.Floating.Popup
                shadow
                onClose={handleClose}
                accessibilityLabel={formatMessage(messages.moreMenu)}
                topOffset={usableNetBannerOffset}
              >
                <VStack gap={theme.spacing['1']}>
                  {moreMenuNavLinks.map((link) => (
                    <NavbarMoreContentLink
                      key={link.url}
                      link={link}
                      isNewLink={unseenLinks.has(link.title)}
                      variant="page"
                      onPress={handleClose}
                    />
                  ))}
                </VStack>

                {isLoggedIn ? (
                  <Navbar.Floating.PopupFooter>
                    <Link asChild withAnchor href={LINKS.ORDER_HISTORY}>
                      <Navbar.Floating.TextLink onPress={handleMoreOrderHistoryPress}>
                        <FormattedMessage {...messages.viewOrderHistory} />
                      </Navbar.Floating.TextLink>
                    </Link>

                    <Link asChild href={LINKS.ACCOUNT}>
                      <Navbar.Floating.TextLink onPress={handleMoreAccountPress}>
                        <FormattedMessage {...messages.myAccount} />
                      </Navbar.Floating.TextLink>
                    </Link>
                  </Navbar.Floating.PopupFooter>
                ) : (
                  <Navbar.Floating.PopupFooter>
                    <Link asChild href={LINKS.AUTH}>
                      <Navbar.Floating.TextLink emphasized onPress={handleLoginSignupPress}>
                        <FormattedMessage {...messages.signInCreateAccount} />
                      </Navbar.Floating.TextLink>
                    </Link>
                  </Navbar.Floating.PopupFooter>
                )}
              </Navbar.Floating.Popup>
            </Navbar.Floating.Container>
          </Portal>
        ) : null}

        <Navbar.Floating.SecondaryNav>
          {isLoggedIn ? null : (
            <Link aria-label={formatMessage(messages.logInOrSignUp)} asChild href={LINKS.AUTH}>
              <Navbar.Floating.AuthButton
                shadow={shouldShowGlassShadow}
                onPress={handleLoginSignupPress}
              >
                <FormattedMessage {...messages.logInOrSignUp} />
              </Navbar.Floating.AuthButton>
            </Link>
          )}

          {shouldShowCurrentOrder && pendingOrder ? (
            <Navbar.Floating.CurrentOrder.Container
              shadow={shouldShowGlassShadow}
              title={`${pendingOrder.etaLabel} • ${pendingOrder.statusLabel}`}
            >
              <Link asChild href={pendingOrder.href}>
                <Navbar.Floating.CurrentOrder.LinkPressable onPress={handleCurrentOrderPress}>
                  {pendingOrder.actionLabel}
                </Navbar.Floating.CurrentOrder.LinkPressable>
              </Link>
            </Navbar.Floating.CurrentOrder.Container>
          ) : null}

          {shouldShowGroupOrderHost && hostGroupOrderDetails ? (
            <Navbar.Floating.GroupOrderHostIndicator
              eyebrow={formatMessage(messages.groupOrderLocationEyebrow, {
                orderChannel: hostGroupOrderDetails.orderChannel,
              })}
              shadow={shouldShowGlassShadow}
              title={hostGroupOrderDetails.locationName}
            />
          ) : null}

          {shouldShowGroupOrderInvitee && inviteeGroupOrderDetails ? (
            <Link asChild href={LINKS.BAG}>
              <Navbar.Floating.GroupOrderInviteeButton
                shadow={shouldShowGlassShadow}
                title={formatMessage(messages.groupOrderInviteeTitle, {
                  host_name: inviteeGroupOrderDetails.hostFirstName,
                })}
                onPress={handleBagPress}
              />
            </Link>
          ) : null}

          {shouldShowLocation ? (
            <View style={styles.locationContainer}>
              <Link
                asChild
                href={locationSelectionHref}
                aria-label={locationTitle}
                disabled={shouldDisableLocationChange}
              >
                <Navbar.Floating.LocationButton
                  eyebrow={locationEyebrow}
                  shadow={shouldShowGlassShadow}
                  title={locationTitle}
                  onPress={handleLocationPress}
                />
              </Link>

              <LocationTooltip
                isVisible={locationTooltip.isVisible}
                orderChannel={orderChannel}
                horizontalAlignment="end"
                horizontalOffset={theme.spacing['3']}
                onDismiss={locationTooltip.dismiss}
              />
            </View>
          ) : null}

          <Link
            aria-label={formatMessage(messages.bagAccessibilityLabel, { count: bagCount })}
            asChild
            href={LINKS.BAG}
          >
            <Navbar.Floating.BagButton
              count={bagCount}
              shadow={shouldShowGlassShadow}
              onPress={handleBagPress}
            />
          </Link>
        </Navbar.Floating.SecondaryNav>
      </Navbar.Floating.Container>

      {shouldRenderStaticContainer ? <View style={staticNavbarContainerStyles} /> : null}
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  staticNavbarContainer: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  staticNavbarContainerBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
  },
  locationContainer: {
    flexShrink: 1,
  },
  drawerAuthLabel: {
    color: theme.colors.WHITE,
  },
});
